import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiClock, FiMapPin, FiZap, FiExternalLink, FiX, FiMaximize2 } = FiIcons;

const News = () => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  
  // Link für das Season Opening 2026
  const eventImage = "https://lima-service.de/Grafiken/SO2026.jpg";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-900"
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://lima-service.de/Grafiken/12.jpg')` }} />
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6">
              <span className="gradient-text">News</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto uppercase tracking-widest font-bold">
              Events & Termine
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-gray-800 rounded-3xl p-8 lg:p-12 border border-orange-500/30 shadow-2xl overflow-hidden relative"
          >
            {/* Background Glow */}
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-500/10 blur-[100px] rounded-full pointer-events-none" />
            
            <div className="flex flex-col lg:flex-row gap-12 items-start relative z-10">
              {/* Image Container - Ohne festes Seitenverhältnis für Originalformat */}
              <motion.div
                className="w-full lg:w-1/2 rounded-2xl overflow-hidden shadow-2xl border border-gray-700 bg-gray-900 group relative cursor-pointer"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                onClick={() => setIsLightboxOpen(true)}
              >
                <img 
                  src={eventImage} 
                  alt="Season Opening 2026" 
                  className="w-full h-auto block"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1558981403-c5f91cbba527?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80";
                  }}
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                  <div className="bg-orange-600 p-4 rounded-full shadow-xl transform scale-75 group-hover:scale-100 transition-transform">
                    <SafeIcon icon={FiMaximize2} className="w-8 h-8 text-white" />
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pointer-events-none">
                  <span className="text-white font-bold flex items-center gap-2">
                    <SafeIcon icon={FiExternalLink} />
                    Originalformat anzeigen
                  </span>
                </div>
              </motion.div>

              {/* Text Content */}
              <div className="w-full lg:w-1/2 space-y-8 lg:sticky lg:top-32">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full border border-orange-500/30 text-orange-400 text-sm font-black uppercase tracking-widest">
                  <SafeIcon icon={FiZap} className="animate-pulse" />
                  <span>Saison-Highlight</span>
                </div>

                <h2 className="text-4xl lg:text-5xl font-black text-white leading-tight uppercase">
                  Season <span className="text-orange-500">Opening</span> 2026
                </h2>

                <div className="space-y-6 text-xl text-gray-300 leading-relaxed">
                  <p className="font-bold text-white text-2xl">
                    Wir starten gemeinsam in die neue Motorrad-Saison!
                  </p>
                  <p>
                    Besuchen Sie uns an unserem großen Eröffnungswochenende. Wir präsentieren die neuesten Custom-Projekte, exklusive Parts und laden zu Benzingesprächen in entspannter Atmosphäre ein. Für das leibliche Wohl ist bestens gesorgt.
                  </p>
                </div>

                {/* Event Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8 border-t border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-700/50 rounded-xl flex items-center justify-center border border-gray-600">
                      <SafeIcon icon={FiCalendar} className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Datum</p>
                      <p className="text-white font-bold">25. & 26. 04. 2026</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-700/50 rounded-xl flex items-center justify-center border border-gray-600">
                      <SafeIcon icon={FiClock} className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Uhrzeit</p>
                      <p className="text-white font-bold">10:00 - 19:00 Uhr</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:col-span-2">
                    <div className="w-12 h-12 bg-gray-700/50 rounded-xl flex items-center justify-center border border-gray-600">
                      <SafeIcon icon={FiMapPin} className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Ort</p>
                      <p className="text-white font-bold">Bavaria Custom Bikes, Ulmer Straße 56, 86660 Tapfheim</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 md:p-12"
            onClick={() => setIsLightboxOpen(false)}
          >
            <motion.button
              className="absolute top-6 right-6 z-[110] p-3 bg-white/10 hover:bg-orange-600 text-white rounded-full transition-all"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                setIsLightboxOpen(false);
              }}
            >
              <SafeIcon icon={FiX} className="w-8 h-8" />
            </motion.button>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-full max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={eventImage} 
                alt="Season Opening 2026 Großansicht" 
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl border border-white/10"
              />
              <div className="absolute -bottom-10 left-0 right-0 text-center text-gray-400 text-sm">
                Zum Schließen klicken oder X benutzen
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default News;